import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Paper,
  TextField,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { Table } from 'src/components/common/Table';
import { WithLoading } from 'src/components/common/WithLoading';
import { useDebounce } from 'src/hooks/useDebounce';
import { useRouter } from 'src/hooks/useRouter';
import { routesMap } from 'src/routes';
import { ResponseWithMeta, useRequest } from 'src/shared/api';
import { formatDate, formatFileSize } from 'src/shared/lib';

const MOBILE_TELEMETRY_SESSIONS_URL =
  'https://api.instock-dv.ru/v1_api_private/crm/mobile-telemetry/sessions';

const APP_VERSION_URL =
  'https://api.instock-dv.ru/v1_api/mobile-sync/app/version';

type MobileTelemetrySession = {
  id: number;
  userId: number;
  userDisplayName: string;
  appVersion: string;
  deviceModel: string;
  osVersion: string;
  type: string;
  data: string | null;
  createdAt: string;
};

type AvailableAppVersion = {
  version: string;
  download_url: string;
  file_size: number;
};

type AppVersionInfo = {
  version: string;
  changelog: string;
  download_url: string;
  required: boolean;
  min_supported_version: string;
  file_size: number;
  platform: string;
  release_date: string;
  available_versions: AvailableAppVersion[];
};

export const ApplicationPage: React.FunctionComponent = () => {
  const { push, query } = useRouter();

  const queryDateFrom = query.dateFrom?.[0] || '-10h';

  const [dateFromInput, setDateFromInput] = useState(queryDateFrom);

  const debouncedDateFromInput = useDebounce<string>(dateFromInput, 500);

  useEffect(() => {
    if (queryDateFrom !== dateFromInput) {
      setDateFromInput(queryDateFrom);
    }
  }, [queryDateFrom]);

  useEffect(() => {
    if ((query.dateFrom?.[0] || '') !== debouncedDateFromInput) {
      push(null, {
        ...query,
        dateFrom: debouncedDateFromInput,
      });
    }
  }, [debouncedDateFromInput]);

  const sessionsRequest = useRequest<ResponseWithMeta<MobileTelemetrySession[]>>(
    MOBILE_TELEMETRY_SESSIONS_URL,
    {
      variables: { dateFrom: debouncedDateFromInput },
    },
  );

  const versionRequest = useRequest<{ data: AppVersionInfo; errors: any[] }>(
    APP_VERSION_URL,
  );

  const versionInfo = versionRequest.data?.data;

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <WithLoading loading={versionRequest.isLoading}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box>Версия: {versionInfo?.version}</Box>
                <Box>
                  Дата релиза:{' '}
                  {versionInfo?.release_date &&
                    formatDate(versionInfo.release_date)}
                </Box>
                <Link
                  href={versionInfo?.download_url}
                  download
                  target="_blank"
                  onClick={e => e.stopPropagation()}
                >
                  Скачать APK
                </Link>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>Размер файла: {formatFileSize(versionInfo?.file_size)}</Box>
              <Box sx={{ whiteSpace: 'pre-wrap' }}>
                Изменения: {versionInfo?.changelog}
              </Box>
              <Table
                title="Доступные версии"
                headers={[
                  {
                    title: 'Версия',
                    extract: (item: AvailableAppVersion) => item.version,
                  },
                  {
                    title: 'Размер',
                    extract: (item: AvailableAppVersion) =>
                      formatFileSize(item.file_size),
                  },
                  {
                    title: 'Скачать',
                    extract: (item: AvailableAppVersion) => (
                      <Link href={item.download_url} download target="_blank">
                        Скачать
                      </Link>
                    ),
                  },
                ]}
                data={versionInfo?.available_versions}
                totalCount={versionInfo?.available_versions?.length}
                loading={versionRequest.isLoading}
              />
            </AccordionDetails>
          </Accordion>
        </WithLoading>
      </Paper>
      <Paper>
        <Box p={2} pb={0}>
          <TextField
            fullWidth
            label="Фильтр времени (dateFrom)"
            value={dateFromInput}
            onChange={e => setDateFromInput(e.target.value)}
            placeholder="-10h"
          />
        </Box>
        <Table
          title="Сессии приложения"
          headers={[
            {
              title: 'id',
              align: 'right',
              extract: (item: MobileTelemetrySession) => item.id,
            },
            {
              title: 'Пользователь',
              extract: (item: MobileTelemetrySession) => (
                <Link
                  href={routesMap.user.getRoute(item.userId)}
                  target="_blank"
                >
                  {item.userDisplayName}
                </Link>
              ),
            },
            {
              title: 'Версия',
              extract: (item: MobileTelemetrySession) => item.appVersion,
            },
            {
              title: 'Устройство',
              extract: (item: MobileTelemetrySession) => item.deviceModel,
            },
            {
              title: 'OS',
              extract: (item: MobileTelemetrySession) => item.osVersion,
            },
            {
              title: 'Тип',
              extract: (item: MobileTelemetrySession) => item.type,
            },
            {
              title: 'Данные',
              extract: (item: MobileTelemetrySession) =>
                item.data ? item.data : '—',
            },
            {
              title: 'Создано',
              extract: (item: MobileTelemetrySession) => item.createdAt,
            },
          ]}
          data={sessionsRequest.data?.data}
          totalCount={sessionsRequest.data?.totalCount}
          loading={sessionsRequest.isLoading}
          onFetch={sessionsRequest.mutate}
          limit={sessionsRequest.data?.totalCount}
          offset={0}
        />
      </Paper>
    </Box>
  );
};
